/**
 * /customer/terms — Terms of Service (Phase 2d).
 *
 * Static client-side placeholder mirroring mobile (customer)/terms.tsx
 * (lines 21–42 SECTIONS array + footer). Operator §10 = "Terms of
 * Service · Version 1.0" verbatim.
 */
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

// Mirror of /app/mobile/app/(customer)/terms.tsx SECTIONS array.
const SECTIONS = [
  {
    heading: '1. Account Usage',
    body: 'Your account is personal and non-transferable. You are responsible for keeping your login credentials secure.',
  },
  {
    heading: '2. Loyalty Stamps',
    body: 'Stamps are issued by participating merchants and represent loyalty rewards, not monetary value. Stamps cannot be exchanged for cash and may expire based on merchant policies.',
  },
  {
    heading: '3. Rewards',
    body: 'Rewards are subject to merchant terms and availability. OneStamp acts as a platform; reward fulfillment is the responsibility of each merchant.',
  },
  {
    heading: '4. Data & Privacy',
    body: 'We collect transaction data to operate the loyalty service. Customer data is not sold to third parties.',
  },
  {
    heading: '5. Updates',
    body: 'These terms may be updated periodically. Continued use of OneStamp implies acceptance of the latest version.',
  },
];

export default function CustomerTermsPage() {
  const navigate = useNavigate();
  return (
    <div className="space-y-6" data-testid="customer-terms-screen">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Back"
          data-testid="customer-terms-back"
          className="w-9 h-9 rounded-full bg-[#15151B] border border-white/10 grid place-items-center text-white/75 hover:text-gold hover:border-gold/35 transition-colors"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-gold">
            Legal
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold leading-tight">
            Terms of Service
          </h1>
        </div>
      </div>

      <section
        className="rounded-3xl border border-gold/25 bg-[#15151B] px-5 md:px-7 py-6"
        data-testid="customer-terms-of-service-content"
      >
        {/* Version badge — mobile-verbatim copy (operator §10) */}
        <div
          className="inline-block text-[10px] font-mono uppercase tracking-[0.18em] text-gold px-3 py-1 rounded-full bg-gold/10 border border-gold/30"
          data-testid="customer-terms-version"
        >
          Terms of Service · Version 1.0
        </div>
        <p className="text-sm text-white/75 leading-relaxed mt-4">
          Welcome to OneStamp. By using this application, you agree to the
          following terms.
        </p>

        <div className="mt-6 space-y-5">
          {SECTIONS.map((s, i) => (
            <div key={s.heading} data-testid={`customer-terms-section-${i}`}>
              <div className="text-sm font-semibold text-white/95">
                {s.heading}
              </div>
              <p className="text-sm text-white/65 leading-relaxed mt-1.5">
                {s.body}
              </p>
            </div>
          ))}
        </div>

        {/* Footer note */}
        <div
          className="mt-7 pt-5 border-t border-white/8 text-xs italic text-white/45 leading-relaxed"
          data-testid="customer-terms-footer-note"
        >
          Full terms and detailed legal pages will be available at a hosted
          URL in future releases.
        </div>
      </section>
    </div>
  );
}
